import { useState, useCallback } from 'react';
import { useConfig } from '../../contexts/ConfigContext';
import { useLog } from '../../contexts/LogContext';
import { UPLOAD_STATUSES } from '../../constants';
import { api } from '../../utils/api';
import type {
  FloorPlanRecord,
  ProjectStatusRecord,
  ProjectUploadStatusRecord,
  UploadStatus,
} from './types';

export const EditFloorPlanContainer = () => {
  const { token, projectId } = useConfig();
  const { log, clearLog } = useLog();

  const [floorPlans, setFloorPlans] = useState<FloorPlanRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [projectStatus, setProjectStatus] =
    useState<ProjectStatusRecord | null>(null);
  const [pendingProjectStatus, setPendingProjectStatus] =
    useState<UploadStatus | null>(null);
  const [isSavingProjectStatus, setIsSavingProjectStatus] = useState(false);

  const [uploadRows, setUploadRows] = useState<ProjectUploadStatusRecord[]>([]);
  const [pendingUploadStatuses, setPendingUploadStatuses] = useState<
    Record<number, UploadStatus>
  >({});
  const [savingUploadId, setSavingUploadId] = useState<number | null>(null);
  const [isFetchingProjectUploadStatus, setIsFetchingProjectUploadStatus] =
    useState(false);

  const fetchProjectAndUploads = useCallback(async () => {
    if (!projectId) return;
    setIsFetchingProjectUploadStatus(true);
    try {
      const [uploadsResponse, projectResponse] = await Promise.all([
        api<{ quantitySurveyorUploads: ProjectUploadStatusRecord[] }>(
          'GET',
          `/api/quantity-surveyor-uploads/project/${projectId}`,
          undefined,
          token,
        ),
        api<{ project: ProjectStatusRecord }>(
          'GET',
          `/api/projects/${projectId}`,
          undefined,
          token,
        ),
      ]);

      if (uploadsResponse.status === 200) {
        const uploads = uploadsResponse.data.quantitySurveyorUploads ?? [];
        setUploadRows(uploads);
        setPendingUploadStatuses(
          Object.fromEntries(uploads.map((row) => [row.id, row.status])),
        );
        if (uploads.length === 0) {
          log('warn', `No upload rows found for project ${projectId}.`);
        }
      } else {
        log(
          'err',
          `Failed to fetch uploads (${uploadsResponse.status}): ${JSON.stringify(uploadsResponse.data)}`,
        );
      }

      if (projectResponse.status === 200) {
        const project = projectResponse.data.project;
        setProjectStatus(project);
        setPendingProjectStatus(project.status);
      } else {
        log(
          'err',
          `Failed to fetch project (${projectResponse.status}): ${JSON.stringify(projectResponse.data)}`,
        );
      }
    } catch (error) {
      log('err', `Error fetching project / uploads: ${(error as Error).message}`);
    } finally {
      setIsFetchingProjectUploadStatus(false);
    }
  }, [projectId, token, log]);

  const handleSaveProjectStatus = useCallback(async () => {
    if (
      !projectStatus ||
      !pendingProjectStatus ||
      pendingProjectStatus === projectStatus.status ||
      isSavingProjectStatus
    )
      return;
    setIsSavingProjectStatus(true);
    try {
      log(
        'step',
        `Updating project #${projectStatus.id} status: ${projectStatus.status} → ${pendingProjectStatus}…`,
      );
      const response = await api<ProjectStatusRecord>(
        'PATCH',
        `/api/projects/${projectStatus.id}/status`,
        { status: pendingProjectStatus },
        token,
      );
      if (response.status === 200) {
        setProjectStatus(response.data);
        setPendingProjectStatus(response.data.status);
        log('ok', `Project status updated to "${response.data.status}".`);
      } else {
        log(
          'err',
          `Failed to update project status (${response.status}): ${JSON.stringify(response.data)}`,
        );
      }
    } catch (error) {
      log(
        'err',
        `Error updating project status: ${(error as Error).message}`,
      );
    } finally {
      setIsSavingProjectStatus(false);
    }
  }, [projectStatus, pendingProjectStatus, isSavingProjectStatus, token, log]);

  const handleSaveUploadStatus = useCallback(
    async (row: ProjectUploadStatusRecord) => {
      const pending = pendingUploadStatuses[row.id];
      if (!pending || pending === row.status || savingUploadId !== null) return;
      setSavingUploadId(row.id);
      try {
        log(
          'step',
          `Updating upload #${row.id} status: ${row.status} → ${pending}…`,
        );
        const response = await api<ProjectUploadStatusRecord>(
          'PATCH',
          `/api/quantity-surveyor-uploads/${row.id}/status`,
          { status: pending },
          token,
        );
        if (response.status === 200) {
          setUploadRows((prev) =>
            prev.map((existing) =>
              existing.id === row.id ? response.data : existing,
            ),
          );
          setPendingUploadStatuses((prev) => ({
            ...prev,
            [row.id]: response.data.status,
          }));
          log('ok', `Upload #${row.id} status updated to "${response.data.status}".`);
        } else {
          log(
            'err',
            `Failed to update upload #${row.id} (${response.status}): ${JSON.stringify(response.data)}`,
          );
        }
      } catch (error) {
        log(
          'err',
          `Error updating upload #${row.id}: ${(error as Error).message}`,
        );
      } finally {
        setSavingUploadId(null);
      }
    },
    [pendingUploadStatuses, savingUploadId, token, log],
  );

  const fetchFloorPlans = useCallback(async () => {
    if (!projectId) {
      log('err', 'Project ID is required to fetch floor plans.');
      return;
    }

    setLoading(true);
    setSelectedId(null);
    clearLog();

    try {
      const [activeResponse, archivedResponse] = await Promise.all([
        api<FloorPlanRecord[]>(
          'GET',
          `/api/quantity-surveyor-floor-plans?projectId=${projectId}`,
          undefined,
          token,
        ),
        api<FloorPlanRecord[]>(
          'GET',
          `/api/quantity-surveyor-floor-plans?projectId=${projectId}&archived=true`,
          undefined,
          token,
        ),
      ]);

      if (activeResponse.status === 200 && archivedResponse.status === 200) {
        const activePlans = activeResponse.data;
        const archivedPlans = archivedResponse.data;
        setFloorPlans([...activePlans, ...archivedPlans]);
        log(
          'ok',
          `Found ${activePlans.length} active and ${archivedPlans.length} archived floor plan(s) for project ${projectId}.`,
        );
      } else {
        const failedResponse =
          activeResponse.status !== 200 ? activeResponse : archivedResponse;
        log(
          'err',
          `Failed to fetch floor plans (${failedResponse.status}): ${JSON.stringify(failedResponse.data)}`,
        );
      }
    } catch (error) {
      log('err', `Error fetching floor plans: ${(error as Error).message}`);
    } finally {
      setLoading(false);
    }

    fetchProjectAndUploads();
  }, [projectId, token, log, clearLog, fetchProjectAndUploads]);

  const handleDelete = useCallback(async () => {
    if (selectedId === null || deleting) return;

    const selectedPlan = floorPlans.find((plan) => plan.id === selectedId);
    if (!selectedPlan) return;

    setDeleting(true);

    try {
      log(
        'step',
        `Deleting floor plan #${selectedId} ("${selectedPlan.name ?? 'unnamed'}")...`,
      );

      const response = await api(
        'DELETE',
        `/api/quantity-surveyor-floor-plans/${selectedId}`,
        undefined,
        token,
      );

      if (response.status === 200) {
        log('ok', `Floor plan #${selectedId} deleted successfully.`);
        setFloorPlans((prev) => prev.filter((plan) => plan.id !== selectedId));
        setSelectedId(null);
      } else {
        log(
          'err',
          `Failed to delete (${response.status}): ${JSON.stringify(response.data)}`,
        );
      }
    } catch (error) {
      log('err', `Error deleting floor plan: ${(error as Error).message}`);
    } finally {
      setDeleting(false);
    }
  }, [selectedId, deleting, floorPlans, token, log]);

  const selectedPlan = floorPlans.find((plan) => plan.id === selectedId);

  const inputClass =
    'bg-[#0d1117] border border-[#30363d] text-[#c9d1d9] px-2.5 py-2 rounded-md text-sm font-mono focus:outline-none focus:border-[#58a6ff]';
  const btnBase =
    'px-5 py-2.5 rounded-md font-semibold text-sm text-white cursor-pointer transition-opacity disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <div>
      <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-4 mb-4">
        <h2 className="text-sm text-[#8b949e] uppercase tracking-wide mb-3">
          Find Floor Plans
        </h2>
        <div className="flex gap-3 items-end">
          <div>
            <label className="block text-[12px] text-[#8b949e] mb-1">
              Project ID
            </label>
            <input
              type="text"
              className={inputClass}
              value={projectId}
              disabled
              style={{ width: 100 }}
            />
          </div>
          <button
            className={`${btnBase} bg-[#1f6feb] hover:opacity-85`}
            onClick={fetchFloorPlans}
            disabled={loading || !projectId}
          >
            {loading ? 'Loading...' : 'Fetch Floor Plans'}
          </button>
        </div>
      </div>

      {(projectStatus || isFetchingProjectUploadStatus) && (
        <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-4 mb-4">
          <h2 className="text-sm text-[#8b949e] uppercase tracking-wide mb-3">
            Project Information
          </h2>
          {isFetchingProjectUploadStatus && !projectStatus && (
            <div className="text-[#8b949e]">Loading project…</div>
          )}
          {projectStatus && (
            <div>
              <div className="text-[#c9d1d9] mb-3">
                <span className="text-[#8b949e]">Project ID:</span>{' '}
                {projectStatus.id}
                {' · '}
                <span className="text-[#8b949e]">Current status:</span>{' '}
                <span className="font-semibold">{projectStatus.status}</span>
                {projectStatus.createdByEmail && (
                  <>
                    {' · '}
                    <span className="text-[#8b949e]">Created by:</span>{' '}
                    {projectStatus.createdByEmail}
                  </>
                )}
                {projectStatus.createdAt && (
                  <>
                    {' · '}
                    <span className="text-[#8b949e]">Created:</span>{' '}
                    {new Date(projectStatus.createdAt).toLocaleString()}
                  </>
                )}
              </div>
              <div className="flex gap-3 items-end flex-wrap">
                <div>
                  <label className="block text-[12px] text-[#8b949e] mb-1">
                    New status
                  </label>
                  <select
                    className={inputClass}
                    value={pendingProjectStatus ?? projectStatus.status}
                    onChange={(e) =>
                      setPendingProjectStatus(e.target.value as UploadStatus)
                    }
                    disabled={isSavingProjectStatus}
                  >
                    {UPLOAD_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  className={`${btnBase} bg-[#1f6feb] hover:opacity-85`}
                  onClick={handleSaveProjectStatus}
                  disabled={
                    isSavingProjectStatus ||
                    !pendingProjectStatus ||
                    pendingProjectStatus === projectStatus.status
                  }
                >
                  {isSavingProjectStatus ? 'Saving…' : 'Save status'}
                </button>
              </div>
              <div className="text-[11px] text-[#8b949e] mt-2">
                Tip: setting the project status to anything other than{' '}
                <code>done</code> hides the project from end users until you set
                it back.
              </div>
            </div>
          )}
        </div>
      )}

      {(uploadRows.length > 0 || isFetchingProjectUploadStatus) && (
        <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-4 mb-4">
          <h2 className="text-sm text-[#8b949e] uppercase tracking-wide mb-3">
            Upload Sessions ({uploadRows.length})
          </h2>
          {uploadRows.map((row) => {
            const pending = pendingUploadStatuses[row.id] ?? row.status;
            const isSavingThisRow = savingUploadId === row.id;
            return (
              <div
                key={row.id}
                className="border-b border-[#21262d] py-3 last:border-b-0"
              >
                <div className="text-[#c9d1d9] mb-2">
                  <span className="text-[#8b949e]">Upload ID:</span> {row.id}
                  {' · '}
                  <span className="text-[#8b949e]">Current status:</span>{' '}
                  <span className="font-semibold">{row.status}</span>
                </div>
                <div className="flex gap-3 items-end flex-wrap">
                  <div>
                    <label className="block text-[12px] text-[#8b949e] mb-1">
                      New status
                    </label>
                    <select
                      className={inputClass}
                      value={pending}
                      onChange={(e) =>
                        setPendingUploadStatuses((prev) => ({
                          ...prev,
                          [row.id]: e.target.value as UploadStatus,
                        }))
                      }
                      disabled={isSavingThisRow}
                    >
                      {UPLOAD_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    className={`${btnBase} bg-[#1f6feb] hover:opacity-85`}
                    onClick={() => handleSaveUploadStatus(row)}
                    disabled={
                      isSavingThisRow ||
                      savingUploadId !== null ||
                      pending === row.status
                    }
                  >
                    {isSavingThisRow ? 'Saving…' : 'Save status'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {floorPlans.length > 0 && (
        <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-4 mb-4">
          <h2 className="text-sm text-[#8b949e] uppercase tracking-wide mb-3">
            Floor Plans ({floorPlans.length})
          </h2>
          <div className="max-h-[400px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[#8b949e] text-left border-b border-[#30363d]">
                  <th className="py-2 px-3 w-[80px]">ID</th>
                  <th className="py-2 px-3">Name</th>
                  <th className="py-2 px-3 w-[180px]">Created</th>
                </tr>
              </thead>
              <tbody>
                {floorPlans.map((plan) => (
                  <tr
                    key={plan.id}
                    className={`border-b border-[#21262d] cursor-pointer transition-colors ${
                      selectedId === plan.id
                        ? 'bg-[#1f6feb22] text-[#58a6ff]'
                        : 'hover:bg-[#21262d]'
                    }`}
                    onClick={() => setSelectedId(plan.id)}
                  >
                    <td className="py-2 px-3 font-mono">{plan.id}</td>
                    <td className="py-2 px-3">
                      {plan.name ?? '(unnamed)'}
                      {plan.archivedAt !== null && (
                        <span className="ml-2 text-[#d29922]">(archived)</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-[#8b949e]">
                      {new Date(plan.createdAt).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedPlan && (
        <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-4 mb-4">
          <h2 className="text-sm text-[#8b949e] uppercase tracking-wide mb-3">
            Selected Floor Plan
          </h2>
          <div className="text-[#c9d1d9] mb-3">
            <span className="text-[#8b949e]">ID:</span> {selectedPlan.id}
            {' · '}
            <span className="text-[#8b949e]">Name:</span>{' '}
            {selectedPlan.name ?? '(unnamed)'}
            {' · '}
            <span className="text-[#8b949e]">Created:</span>{' '}
            {new Date(selectedPlan.createdAt).toLocaleString()}
            {selectedPlan.archivedAt !== null && (
              <>
                {' · '}
                <span className="text-[#d29922]">
                  Archived {new Date(selectedPlan.archivedAt).toLocaleString()}
                </span>
              </>
            )}
          </div>
          <button
            className={`${btnBase} bg-[#da3633] hover:opacity-85`}
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : `Delete Floor Plan #${selectedPlan.id}`}
          </button>
        </div>
      )}
    </div>
  );
};
